import React, { useState, useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import { Button, Col, Form, FormFeedback, Input, Label } from 'reactstrap';
import { useMutation, useQuery } from '@tanstack/react-query';
import { toast } from 'react-toastify';
import Icon from '@mdi/react';
import { mdiChevronLeft, mdiStar } from '@mdi/js';
import { useForm, Controller } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import { applyWfh } from '../../../store/apis/wfhService';
import { fetchLeaveByTransactionId } from '../../../store/apis/leaveService';
import {
  initialValues,
  validationSchema,
  populateFormValues,
} from './constants';
import {
  ErrorModalWrapper,
  ErrorTable,
  DatePicker,
} from '../../../components/Common';
import { getCompanyCode } from '../../../config/axios.config';
import { companySet1 } from '../constants';

import '../index.scss';

function ApplyWFH() {
  document.title = 'Apply Work From Home | Envision - Daikin';
  const location = useLocation();
  const query = new URLSearchParams(location.search);
  const { CoCd, ID } = Object.fromEntries(query);
  const [errorModal, setErrorModal] = useState(false);
  const [errorData, setErrorData] = useState(null);
  const formType = ID ? 'view' : 'apply';
  const selectedCoCd = getCompanyCode();

  const toggleErrorModal = () => setErrorModal(!errorModal);

  const {
    control,
    handleSubmit,
    watch,
    reset,
    setError,
    clearErrors,
    formState: { errors, isValid },
  } = useForm({
    mode: 'onChange',
    resolver: yupResolver(validationSchema),
    defaultValues: initialValues,
  });

  const calculateDays = (startDate, endDate) => {
    if (!startDate || !endDate) {
      return 0;
    }
    const start = new Date(startDate);
    const end = new Date(endDate);
    const timeDiff = end.getTime() - start.getTime();
    const dayDiff = timeDiff / (1000 * 3600 * 24);
    return dayDiff + 1;
  };

  const { mutate: applyWfhMutation, isPending: applyWfhPending } = useMutation({
    mutationFn: payload => applyWfh(payload),
    onSuccess: data => {
      if (data.flag === 'True') {
        toast.success(
          `WFH Request #${data?.transactionNo} Has Been Submitted Successfully`,
          {
            className: 'toast-success',
          },
        );
        reset();
      } else if (data.flag === 'False') {
        if (
          data?.message === 'Leave cannot be applied for Prior Calendar Year.'
        ) {
          toast.error('Leave cannot be applied for Prior Calendar Year.', {
            className: 'toast-error',
          });
          setError('leaveFromDate');
          setError('leaveToDate');
          data.isTable = false;
        } else {
          data.isTable = true;
          setErrorModal(true);
          setErrorData(data);
        }
      }
    },
    onError: error => {
      toast.error(error.message, {
        className: 'toast-error',
      });
    },
  });

  const { data: wfhData } = useQuery({
    queryKey: ['wfhData', ID],
    queryFn: () =>
      fetchLeaveByTransactionId({
        transNo: ID,
        companyCode: CoCd,
      }),
    enabled: formType === 'view',
  });

  const onSubmit = async values => {
    const additionalData = {
      leaveType: 'WH',
      altrBackupPlan: '',
      attachmentPath: '',
      entitledDays: 0.0,
      usedDays: 0.0,
      balanceDays: 0.0,
      extraDays: 0.0,
      leaveFromTime: '00:00:00',
      leaveToTime: '00:00:00',
      delegateApprover: 0,
    };

    const payload = {
      ...values,
      leaveFromDate: new Date(values.leaveFromDate).toISOString(),
      leaveToDate: new Date(values.leaveToDate).toISOString(),
      ...additionalData,
    };

    applyWfhMutation(payload);
  };

  const watchValues = watch();

  useEffect(() => {
    if (formType === 'view' && wfhData && wfhData.length > 0) {
      const editFormValues = populateFormValues(wfhData?.[0]);
      reset(editFormValues);
    }
  }, [formType, wfhData]);

  useEffect(() => {
    if (errorData && !errorData?.isTable) {
      toast.error(errorData?.message || 'An error occurred', {
        className: 'toast-error',
      });
    }
  }, [errorData]);

  useEffect(() => {
    const { leaveFromDate, leaveToDate } = watchValues;

    // only run if both dates are present
    if (leaveFromDate && leaveToDate) {
      const from = new Date(leaveFromDate);
      const to = new Date(leaveToDate);

      if (to < from) {
        setError('leaveFromDate', {
          type: 'manual',
          message: 'From date cannot be after To date',
        });
        setError('leaveToDate', {
          type: 'manual',
          message: 'To date cannot be before From date',
        });
      } else {
        clearErrors(['leaveFromDate', 'leaveToDate']); // safe only if valid
      }
    }
  }, [
    watchValues.leaveFromDate,
    watchValues.leaveToDate,
    setError,
    clearErrors,
  ]);

  return (
    <div className="page-content apply-forms px-0">
      <div className="d-flex flex-row p-0">
        <div className="col-7 p-0 h-100">
          <div className="d-flex flex-row align-items-center justify-content-between leave-layout-border-bottom">
            {formType !== 'view' ? (
              <div className="ps-3 py-3 d-flex align-items-center fs-5 fw-semibold apply-header">
                Apply Work From Home
              </div>
            ) : (
              <div className="ps-1 py-3 d-flex flex-row align-items-center gap-2 apply-header">
                <button
                  type="button"
                  className="btn btn-link p-0 back-btn"
                  onClick={() => window.history.back()}
                >
                  <Icon path={mdiChevronLeft} size={1.1} />
                  <span className="back fs-5">Back</span>
                </button>
                <div
                  style={{
                    width: '1px',
                    borderLeft: '1px solid lightgrey',
                    height: '20px',
                  }}
                />
                <div className="d-flex flex-row gap-1">
                  <span className="request-id-span fs-5">Request ID</span>
                  <span className="request-id-span-value fs-5">{ID}</span>
                </div>
              </div>
            )}{' '}
          </div>
          <Form
            className="d-flex flex-column gap-2 p-3"
            onSubmit={handleSubmit(onSubmit)}
          >
            <div className="d-flex flex-row gap-2">
              <Col>
                <div>
                  <Label className="my-1">
                    <span>From Date</span>
                    <span className="text-danger">*</span>
                  </Label>
                  <Controller
                    name="leaveFromDate"
                    control={control}
                    render={({ field }) => (
                      <DatePicker
                        {...field}
                        disabled={formType === 'view'}
                        className={`external-filter-input ${errors.leaveFromDate ? 'is-invalid' : ''}`}
                        error={!!errors.leaveFromDate}
                        title="Select the start date for your Work From Home period."
                      />
                    )}
                  />
                  {errors.leaveFromDate && (
                    <div className="invalid-feedback d-block">
                      {errors.leaveFromDate.message}
                    </div>
                  )}
                </div>
              </Col>
              <Col>
                <div>
                  <Label className="my-1">
                    <span>To Date</span>
                    <span className="text-danger">*</span>
                  </Label>
                  <Controller
                    name="leaveToDate"
                    control={control}
                    render={({ field }) => (
                      <DatePicker
                        {...field}
                        disabled={formType === 'view'}
                        className={`external-filter-input ${errors.leaveToDate ? 'is-invalid' : ''}`}
                        error={!!errors.leaveToDate}
                        title="Select the end date for your Work From Home period."
                      />
                    )}
                  />
                  {errors.leaveToDate && (
                    <div className="invalid-feedback d-block">
                      {errors.leaveToDate.message}
                    </div>
                  )}
                </div>
              </Col>
            </div>
            <Col>
              <div>
                <Label className="my-1">
                  <span>Days</span>
                </Label>
                <Input
                  readOnly
                  placeholder="Enter Days"
                  value={calculateDays(
                    watchValues.leaveFromDate,
                    watchValues.leaveToDate,
                  )}
                  disabled
                  className="form-control external-filter-input"
                  title="Auto-calculated - Total number of WFH days being requested."
                />
              </div>
            </Col>
            <Col>
              <div>
                <Label className="my-1">
                  <span>Reasons for WFH</span>
                  <span className="text-danger">*</span>
                </Label>
                <Controller
                  name="reasonForLeave"
                  control={control}
                  render={({ field }) => (
                    <Input
                      type="text"
                      className={`form-control external-filter-input ${errors.reasonForLeave && 'is-invalid'}`}
                      placeholder="Enter Reason for WFH"
                      {...field}
                      disabled={formType === 'view'}
                      title="Enter the reason for requesting Work From Home."
                    />
                  )}
                />
                {errors.reasonForLeave && (
                  <FormFeedback>{errors.reasonForLeave.message}</FormFeedback>
                )}
              </div>
            </Col>
            <Col>
              <div>
                <Label className="my-1">Comments</Label>
                <Controller
                  name="comments"
                  control={control}
                  render={({ field }) => (
                    <Input
                      type="textarea"
                      rows={3}
                      placeholder="Enter Comments"
                      className="form-control external-filter-input"
                      id="comments"
                      {...field}
                      disabled={formType === 'view'}
                      title="Add any extra details or context for your WFH request."
                    />
                  )}
                />
              </div>
            </Col>
            {formType !== 'view' && (
              <div className="d-flex flex-row align-items-center justify-content-center gap-2 p-3">
                <Button
                  className="custom-light-btn"
                  style={{
                    width: '25%',
                  }}
                  onClick={() => reset()}
                  disabled={formType === 'view'}
                >
                  Clear
                </Button>
                <Button
                  className="custom-btn"
                  style={{
                    width: '25%',
                  }}
                  type="submit"
                  disabled={!isValid || formType === 'view' || applyWfhPending}
                >
                  <span>{applyWfhPending ? 'Submitting...' : 'Submit'}</span>
                </Button>
              </div>
            )}
          </Form>
        </div>
        <div
          className="col-5 leave-layout-border-left"
          style={{
            color: 'black',
          }}
        >
          <div className="tnc-wrapper px-3 pt-3">
            <b className="tnc-title mb-1">Terms & Conditions</b>
            <ul>
              <li>Max. 2 days per week.</li>
              <li>Not applicable for service field technicians</li>
              <li>
                Manager must approve in advance (S/he can reject anytime if
                impacting efficiency)
              </li>
              <li>
                Employees choosing to work from another country need approval
                from HR and Head of Function(max 1 week)
              </li>
            </ul>
            {companySet1.includes(selectedCoCd) && (
              <div className="d-flex flex-row align-items-center gap-1">
                <Icon path={mdiStar} size={1} color="#DC6803" />
                <span>Please</span>
                <a
                  href="https://denv.sharepoint.com/sites/eu-intra-dame-hr-en/SitePages/Remote-Work-Policy.aspx"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="clickable"
                >
                  click here
                </a>
                <span>to view the leave policy in detail.</span>
              </div>
            )}
          </div>
        </div>
        {errorData && errorData?.isTable && (
          <ErrorModalWrapper
            isModalOpen={errorModal}
            toggle={toggleErrorModal}
            errorMessage={errorData?.message}
            onButtonClick={toggleErrorModal}
            size="xl"
          >
            {errorData?.isTable && (
              <ErrorTable errorData={errorData?.errorData} />
            )}
          </ErrorModalWrapper>
        )}
      </div>
    </div>
  );
}

export default ApplyWFH;
